import { apiErrorText } from '@tendedloop/shared/i18n';
import i18n from '@/i18n';

// VITE_API_URL is set to the root (e.g. https://api.tendedloop.com), append /api
const rawUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001';
const API_BASE_URL = rawUrl.endsWith('/api') ? rawUrl : `${rawUrl}/api`;

interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
}

class ApiClient {
  private baseUrl: string;
  private accessToken: string | null = null;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
    this.accessToken = localStorage.getItem('scout_accessToken');
  }

  setAccessToken(token: string | null) {
    this.accessToken = token;
    if (token) {
      localStorage.setItem('scout_accessToken', token);
    } else {
      localStorage.removeItem('scout_accessToken');
    }
  }

  getAccessToken(): string | null {
    return this.accessToken;
  }

  private buildUrl(endpoint: string, params?: Record<string, string | number | boolean | undefined>): string {
    const url = new URL(`${this.baseUrl}${endpoint}`);
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined) {
          url.searchParams.append(key, String(value));
        }
      });
    }
    return url.toString();
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit & { params?: Record<string, string | number | boolean | undefined> } = {},
  ): Promise<T> {
    const { params, ...fetchOptions } = options;
    const url = this.buildUrl(endpoint, params);

    const headers: HeadersInit = {
      ...(fetchOptions.body ? { 'Content-Type': 'application/json' } : {}),
      ...options.headers,
    };

    if (this.accessToken) {
      (headers as Record<string, string>)['Authorization'] = `Bearer ${this.accessToken}`;
    }

    const response = await fetch(url, {
      ...fetchOptions,
      headers,
    });

    const data: ApiResponse<T> = await response.json();

    if (!response.ok || !data.success) {
      if (response.status === 401) {
        const refreshed = await this.refreshToken();
        if (refreshed) {
          (headers as Record<string, string>)['Authorization'] = `Bearer ${this.accessToken}`;
          const retryResponse = await fetch(url, { ...fetchOptions, headers });
          const retryData: ApiResponse<T> = await retryResponse.json();
          if (!retryResponse.ok || !retryData.success) {
            throw new Error(apiErrorText(i18n, retryData, i18n.t('errors:generic.tryAgain', 'Please try again')));
          }
          return retryData.data as T;
        }
        this.setAccessToken(null);
        localStorage.removeItem('scout_refreshToken');
        window.location.href = '/login';
      }
      throw new Error(apiErrorText(i18n, data, i18n.t('errors:generic.tryAgain', 'Please try again')));
    }

    return data.data as T;
  }

  private async refreshToken(): Promise<boolean> {
    const refreshToken = localStorage.getItem('scout_refreshToken');
    if (!refreshToken) return false;

    try {
      const response = await fetch(`${this.baseUrl}/scout/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
      });

      const data = await response.json();
      if (data.success && data.data?.accessToken) {
        this.setAccessToken(data.data.accessToken);
        if (data.data.refreshToken) {
          localStorage.setItem('scout_refreshToken', data.data.refreshToken);
        }
        return true;
      }
      return false;
    } catch {
      return false;
    }
  }

  async get<T>(endpoint: string, params?: Record<string, string | number | boolean | undefined>): Promise<T> {
    return this.request<T>(endpoint, { method: 'GET', params });
  }

  async post<T>(endpoint: string, body?: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  async patch<T>(endpoint: string, body?: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PATCH',
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  async delete<T>(endpoint: string, body?: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'DELETE',
      body: body ? JSON.stringify(body) : undefined,
    });
  }
}

export const api = new ApiClient(API_BASE_URL);
export type { ApiResponse };
